import { X } from 'lucide-react'
import type { ReactNode } from 'react'

import Reveal from '@/components/motion/Reveal'
import IconButton from '@/components/ui/IconButton'
import {
  STATE_ART,
  isFailureKind,
  stateActionClass,
  stateCopy,
  type StateKind,
  type StateSize,
} from '@/components/ui/empty-state-variants'
import { cn } from '@/lib/utils'
import { savedAtMeta } from '@/lib/view-state'

/*
 * Every optional prop is spelled `| undefined`: under exactOptionalPropertyTypes
 * a wrapper forwarding its own optional prop (CardState, QueryState,
 * NotAvailable) could not otherwise pass it straight through. ChartState
 * records the same rule.
 */
export interface EmptyStateProps {
  /** What happened: picks the default copy, art and live-region role. Default `empty`. */
  kind?: StateKind | undefined
  /** Where it sits: picks the art size, type, spacing and buttons. Default `section`. */
  size?: StateSize | undefined
  /**
   * Stands alone: shows the art, an h2 and its own padding; no card fill or
   * border (owner, 2026-10-13: every no-data state looks like the generic one).
   * TODO(tech-debt): rename to `standalone` — the name predates dropping the card fill.
   */
  framed?: boolean | undefined
  /** `banner` puts the art left of the copy (Home's empty watchlist). Page and section only. */
  layout?: 'stack' | 'banner' | undefined
  /**
   * Defaults to the kind's own title. Pass your own and the default
   * description stays out too — it was written to follow the default title.
   */
  title?: string | undefined
  /** `null` for none. */
  description?: ReactNode
  /** `not-available`: completes "{thing} isn't live yet. It will show here as soon as it is." */
  thing?: string | undefined
  /** `no-results`: the search text, quoted in the default title. */
  query?: string | undefined
  /** A quiet line under the description — `lastUpdatedMeta()` from `@/lib/view-state`. */
  meta?: ReactNode
  /**
   * `offline`: when the copy the member can fall back on was fetched, a query's
   * `dataUpdatedAt`. Draws "Saved at 03:00 PM, 2 Bhadra 2083" as the meta line.
   * 0 (nothing was ever saved) or a `meta` of your own leaves it out.
   */
  savedAt?: number | undefined
  /** A sentence-case line above the title ("Your watchlist"). */
  eyebrow?: string | undefined
  /**
   * `failed` / `offline`: draws the standard "Try again". Omit it and the
   * message shows with no button rather than a dead one. Ignored on the other
   * kinds, which have nothing to retry.
   */
  onRetry?: (() => void) | undefined
  /** A retry is in flight: "Trying…", and clicks are ignored. */
  retrying?: boolean | undefined
  /**
   * `offline`: draws "Show last close" beside "Try again". Pass it only where a
   * saved copy of the last close exists to show (the Market boards); everywhere
   * else the retry is the only action.
   */
  onShowLastClose?: (() => void) | undefined
  /** The caller's primary action. Style it with `stateActionClass(size)`. */
  action?: ReactNode
  /** The quieter one beside it: `stateActionClass(size, 'secondary')`. */
  secondaryAction?: ReactNode
  /**
   * A mascot `src`. Left out, the kind's default shows only when the block
   * stands alone (`page`, `framed` or `banner`) — inside another card or a
   * table it would be noise. `null` for none. Not drawn at `compact`.
   */
  art?: string | null | undefined
  /** A glyph in place of the art. `null` hides the failure dot too. */
  icon?: ReactNode
  /** Default: `h2` when the block stands alone (`framed`) or is the page, else `p`. */
  headingLevel?: 'h1' | 'h2' | 'h3' | 'p' | undefined
  /**
   * `roomy` (rest:al, the Alerts mock's empty page): at `section` size, a 160 mascot, a 16px body
   * and 120 above and below. Default `standard`, unchanged for every other caller.
   */
  scale?: 'standard' | 'roomy' | undefined
  /** Draws a × in the top-right corner. Meant for banners. */
  onDismiss?: (() => void) | undefined
  className?: string | undefined
  /** Anything the state needs under its actions: a picker, suggestion chips. */
  children?: ReactNode
}

type ArtScale = 'stack' | 'inline'

/*
 * `page` and `section` are one stack (`5327:4326`): mascot, 12, a 20px title,
 * 8, the body, 12, the meta line, 16, the buttons. A page stands unframed on
 * the page; a section is the same stack in its tab.
 */
const CONTAINER: Record<StateSize, string> = {
  // The frame's text column: its body line wraps at ~475px.
  page: 'mx-auto flex w-full max-w-120 flex-col items-center justify-center gap-3 text-center',
  section: 'flex w-full flex-col items-center justify-center gap-3 px-6 py-10 text-center',
  inline: 'flex w-full gap-4 text-left',
  compact: 'flex size-full flex-col items-center justify-center gap-2 text-center',
}

// A `page` block that stands alone (`framed`) keeps the old 560px column: 32px in, a 496px column.
const PAGE_FRAMED = 'max-w-140 p-8'

/**
 * `page`'s own outer stage — `layout="stack"` only (never `banner`, whatever
 * its size; see the `staged` guard below). Wraps the block; is not the block.
 *
 * CONTAINER.page's own `justify-center` centres its CHILDREN, not the block
 * itself — a content-height box handing `justify-center` down is a no-op.
 * On a screen where the block is the only thing in the main column, nothing
 * upstream of it claims the column's own height either, so it rendered as a
 * small block pinned to the top of however much empty space the column
 * happened to have (owner's report: "the empty screen card... like card at
 * the top, no balancing element"). This stage is what claims that height;
 * the block stays exactly as tall as its content and is centred INSIDE the
 * stage rather than stretched to fill it — a stretched card is a different,
 * worse bug (a 700px card with three lines floating in the middle of it).
 *
 * `min-h-[calc(60svh-var(--header-h)-var(--space-6))]`:
 *  - `svh`, not `vh`: mobile Safari/Chrome size `vh` off the LARGEST possible
 *    viewport (address bar hidden), so a `vh` figure overflows into a
 *    scrollbar the moment that bar is actually showing. `svh` is the
 *    smallest-viewport figure and can never do that.
 *  - `var(--header-h)` + `var(--space-6)`: the same pair `ScreenLayout`'s own
 *    sticky rail offset subtracts for "the fixed top nav, plus the one gap
 *    above where the main column's content begins"
 *    (`top-[calc(var(--header-h)+var(--space-6))]`). What neither formula can
 *    subtract is the screen's own `header` slot — a bare `TitleRow` is one
 *    36px line, `PortfolioFamilyHeader` stacks a second row under it — since
 *    that slot is a sibling this component never sees.
 *  - `60`, not `100`, percent of what's left: claiming the whole remainder
 *    would, once that unmeasurable header slot is added back on top, overflow
 *    a short (~660px) window into the scrollbar this value exists to avoid.
 *    60svh leaves headroom for the tallest header row this app has even on a
 *    660px window, while still pulling the block well clear of the top on
 *    anything taller — see the build report for the arithmetic.
 *
 * `pt-8 pb-16`, not symmetric: a block truly centred in a tall box reads as
 * sitting slightly LOW — the eye weighs the space below it more than the
 * space above. Less padding above than below is the standard correction, and
 * is what lifts the block a little above true centre here.
 *
 * `w-full`: without it, this stage shrinks to fit whenever its own parent
 * does not stretch its children (root.tsx's error/not-found `<main>` is
 * `flex items-center justify-center`, not a column) — and the block's own
 * `w-full max-w-120` one level down would then have no definite width to
 * resolve against. Every other caller already stretches this via
 * `ScreenLayout`'s column flex, so this is redundant there, not wrong.
 */
const PAGE_STAGE =
  'flex w-full min-h-[calc(60svh-var(--header-h)-var(--space-6))] flex-col justify-center pt-8 pb-16'

// Home's empty-watchlist frame (`4236:70979`): 844×168, the 120px art 24px in
// and centred, the copy 24px right of it. In a narrow box that geometry leaves
// the copy ~100px, so it tightens there. Measured on the container, not the
// viewport: a banner in a half-width card on a desktop is as narrow as one on
// a phone. `@md` (448px) keeps every phone's main column on the tight side, as
// the viewport `sm:` did, and never hands the copy less than ~210px.
const BANNER = 'relative flex w-full items-center gap-4 px-5 py-8 text-left @md:gap-6 @md:p-6'
const BANNER_ART = 'size-20 @md:size-30'

const COPY: Record<StateSize, string> = {
  page: 'flex flex-col items-center gap-2',
  section: 'flex flex-col items-center gap-2',
  inline: 'flex min-w-0 flex-1 flex-col',
  compact: 'flex flex-col items-center',
}

/*
 * Page and section: the 20px section rung, one step under the page's 32px
 * title. Inline stays at 14/600 so it never outranks the host card's own title.
 */
const TITLE: Record<StateSize, string> = {
  page: 'text-h-section text-foreground',
  section: 'text-h-section text-foreground',
  inline: 'text-h-s text-foreground',
  compact: 'text-label-m text-muted-foreground',
}

const BODY: Record<StateSize, string> = {
  page: 'text-body-m text-muted-foreground',
  section: 'max-w-90 text-body-m text-muted-foreground',
  inline: 'text-body-s text-muted-foreground',
  compact: 'text-body-xs text-muted-foreground',
}

const ACTIONS: Record<StateSize, string> = {
  page: 'mt-1 flex flex-wrap items-center justify-center gap-3',
  section: 'mt-1 flex flex-wrap items-center justify-center gap-3',
  inline: 'flex shrink-0 items-center gap-3',
  compact: 'flex items-center gap-3',
}

// Also written as width/height attributes, so the box is reserved before the PNG decodes.
const ART: Record<ArtScale, { px: number; className: string }> = {
  stack: { px: 120, className: 'size-30' },
  inline: { px: 56, className: 'size-14' },
}

const isBlank = (node: ReactNode): boolean =>
  node === undefined || node === null || node === false || node === ''

/**
 * A failure is announced; an empty list is not — it is ordinary page content.
 * Assertive only for a whole-page failure: when the network drops, the four
 * rail cards and a chart-heavy table would otherwise interrupt the reader
 * several times over. No-results is polite, since it replaces what the member
 * was just typing towards.
 */
function liveRole(
  kind: StateKind,
  size: StateSize,
  banner: boolean,
): 'alert' | 'status' | undefined {
  if (isFailureKind(kind)) return size === 'page' && !banner ? 'alert' : 'status'
  if (kind === 'no-results') return 'status'
  return undefined
}

/**
 * The one block for "there is nothing to show here" — empty, no results, not
 * built yet, not found, failed or offline — at four scales, so no screen
 * hand-rolls its own.
 *
 * Called with only `title`/`description`/`action`, as every screen did before
 * `kind` existed, it renders what it always did (in the current type scale):
 * no art, no role, no added copy.
 */
export default function EmptyState({
  kind = 'empty',
  size = 'section',
  framed = false,
  layout = 'stack',
  title,
  description,
  thing,
  query,
  meta,
  savedAt,
  eyebrow,
  onRetry,
  retrying = false,
  onShowLastClose,
  action,
  secondaryAction,
  scale = 'standard',
  art,
  icon,
  headingLevel,
  onDismiss,
  className,
  children,
}: EmptyStateProps) {
  const banner = layout === 'banner' && (size === 'page' || size === 'section')
  // The only case PAGE_STAGE wraps — see its own doc comment for why `banner`
  // is excluded even at `page` size, and why every other size never applies.
  const staged = size === 'page' && !banner
  const roomy = scale === 'roomy' && size === 'section'
  const stacked = !banner && (size === 'page' || size === 'section')
  const failure = isFailureKind(kind)
  const copy = stateCopy(kind, { query, thing })

  const shownTitle = title ?? copy.title
  const shownDescription =
    description !== undefined
      ? description
      : title !== undefined || size === 'compact'
        ? null
        : copy.description
  const shownMeta =
    isBlank(meta) && kind === 'offline' && savedAt !== undefined ? savedAtMeta(savedAt) : meta

  const artScale: ArtScale | null =
    size === 'compact' ? null : size === 'inline' && !banner ? 'inline' : 'stack'
  const standsAlone = size === 'page' || framed || banner
  const artSrc =
    artScale === null ? null : art === undefined ? (standsAlone ? STATE_ART[kind] : null) : art
  const hasArt = icon === undefined && artSrc !== null

  let visual: ReactNode = null
  if (icon !== undefined) {
    visual = icon
  } else if (artSrc !== null && artScale !== null) {
    visual = (
      <img
        src={artSrc}
        alt=""
        width={roomy && artScale === 'stack' ? 160 : ART[artScale].px}
        height={roomy && artScale === 'stack' ? 160 : ART[artScale].px}
        decoding="async"
        className={cn(
          banner ? BANNER_ART : roomy && artScale === 'stack' ? 'size-40' : ART[artScale].className,
          'shrink-0 object-contain',
        )}
      />
    )
  } else if (failure) {
    // Destructive for a failure, warning for offline, never market-down: a
    // request that failed is not a price that fell. Offline wears RefreshBar's
    // own tone for the same state.
    visual = (
      <span
        aria-hidden="true"
        className={cn(
          'size-2 shrink-0 rounded-full',
          kind === 'offline' ? 'bg-warning-fill' : 'bg-destructive-fill',
          // Level with the title's first line, not the middle of a two-line block.
          size === 'inline' ? 'mt-2' : null,
        )}
      />
    )
  }

  const Heading = headingLevel ?? (framed || size === 'page' ? 'h2' : 'p')
  const heading = (
    <Heading className={banner ? 'text-h-section text-foreground' : TITLE[size]}>
      {shownTitle}
    </Heading>
  )

  const copyBlock = (
    <div className={banner ? 'flex flex-col gap-1' : COPY[size]}>
      {eyebrow === undefined ? (
        heading
      ) : (
        <div
          className={cn(
            'flex flex-col',
            banner ? 'gap-2' : 'gap-1',
            banner || size === 'inline' ? null : 'items-center',
          )}
        >
          <p className="text-body-xs text-muted-foreground-subtle">{eyebrow}</p>
          {heading}
        </div>
      )}
      {isBlank(shownDescription) ? null : (
        <p
          className={
            banner
              ? 'text-body-m text-muted-foreground'
              : roomy
                ? 'max-w-90 text-body-l text-muted-foreground'
                : BODY[size]
          }
        >
          {shownDescription}
        </p>
      )}
      {isBlank(shownMeta) ? null : (
        <p className={cn('text-body-xs text-muted-foreground-subtle', stacked ? 'mt-1' : null)}>
          {shownMeta}
        </p>
      )}
    </div>
  )

  const retry =
    failure && onRetry !== undefined ? (
      <button
        type="button"
        className={stateActionClass(size)}
        aria-disabled={retrying || undefined}
        onClick={() => {
          // Ignored rather than `disabled`: disabling the button the member
          // just pressed drops their keyboard focus to <body>.
          if (!retrying) onRetry()
        }}
      >
        {retrying ? 'Trying…' : 'Try again'}
      </button>
    ) : null

  const showLastClose =
    kind === 'offline' && onShowLastClose !== undefined ? (
      <button
        type="button"
        className={stateActionClass(size, 'secondary')}
        onClick={onShowLastClose}
      >
        Show last close
      </button>
    ) : null

  const actions =
    retry === null &&
    showLastClose === null &&
    isBlank(action) &&
    isBlank(secondaryAction) ? null : (
      <div
        className={cn(
          banner ? 'mt-2 flex flex-wrap items-center gap-2' : ACTIONS[size],
          size === 'inline' && !hasArt ? 'mt-1' : null,
        )}
      >
        {retry}
        {showLastClose}
        {action}
        {secondaryAction}
      </div>
    )

  const content = (
    <Reveal
      as="div"
      variant="fade-up"
      // Plays on mount only, so a re-render never replays it. A staged page
      // block animates its stage instead (below): this one would otherwise
      // rise inside a stage the page's stagger is already rising.
      entered={staged}
      role={liveRole(kind, size, banner)}
      // While a retry runs the region holds its announcement; the outcome is
      // read once it settles, so a second failure is heard again.
      aria-busy={retrying || undefined}
      className={cn(
        banner ? BANNER : CONTAINER[size],
        roomy && !banner ? 'py-30' : null,
        size === 'inline' ? (hasArt ? 'items-center' : 'items-start') : null,
        framed && size === 'page' && !banner ? PAGE_FRAMED : null,
        framed && size === 'inline' ? 'p-4' : null,
        // Room for the 36px close, 24px in from the corner.
        onDismiss ? 'relative pr-16 @md:pr-16' : null,
        className,
      )}
    >
      {visual}
      {banner ? (
        <div className="flex min-w-0 flex-1 flex-col items-start">
          {copyBlock}
          {actions}
        </div>
      ) : (
        <>
          {copyBlock}
          {actions}
        </>
      )}
      {children}
      {onDismiss ? (
        <IconButton
          aria-label="Dismiss"
          onClick={onDismiss}
          className="absolute top-6 right-6 size-9"
        >
          <X className="size-4" aria-hidden="true" />
        </IconButton>
      ) : null}
    </Reveal>
  )

  // Unstaged sizes, and banner, return `content` untouched — see PAGE_STAGE's
  // own doc comment for why only a plain `page` block gets wrapped.
  return staged ? (
    <Reveal variant="fade-up" className={PAGE_STAGE}>
      {content}
    </Reveal>
  ) : (
    content
  )
}
